import * as React from "react";
import { connect } from "react-redux";
import * as cn from "classnames/bind";
import { CalendarState, SpiralItem } from "burde";
import { ApplicationState } from "store";
import { getCalendar, actionCreators } from "page/calendar/store";
import { actionCreators as uiActionCreators, getSpiralItem } from "ui/store";
import * as styles from "./owner-page.css";
import { getSinglePagePreviewContainer } from "svg/draw/helpers/preview-helper";
import { elementIdentifiers } from "svg/draw/dynamic-layout/constants";
import { D3Selection } from "svg/draw/draw-calendar";
import { OwnerPage as OwnerPageType } from "burde";
import { getOwnerPage, getOwnerPageImageUrl } from "burde-api-content/store";
import { useEffect, useState } from "react";

const cx = cn.bind(styles);

type OwnerPageProps = {
    calendar: CalendarState;
    ownerPage: OwnerPageType | null;
    ownerPageImageUrl: string | null;
    spiralItem: SpiralItem | null;
} & typeof actionCreators &
    typeof uiActionCreators;

const OwnerPage: React.FC<OwnerPageProps> = ({
    calendar,
    ownerPage,
    ownerPageImageUrl,
    spiralItem,
}) => {
    const [divRef, setDivRef] = useState<HTMLDivElement | null>(null);

    const drawOwnerPage = async (ref: HTMLDivElement | null) => {
        const spiralSettings = {
            active: true,
            positionToRight: false,
        };

        let previewContainer = getSinglePagePreviewContainer(
            ref,
            calendar,
            spiralSettings,
            spiralItem,
        );

        if (!previewContainer || ownerPage === null) return;

        const ownerPageSvg = ownerPage.endpoints.svgData;

        previewContainer
            .append("g")
            .html(ownerPageSvg !== undefined ? ownerPageSvg : "");

        updateName(previewContainer, calendar);
        updateEmail(previewContainer, calendar);
        updatePhoneNumber(previewContainer, calendar);
        updateGreeting(previewContainer, calendar);

        if (ownerPageImageUrl !== null) {
            updateImage(previewContainer, ownerPageImageUrl);
        }
        // Remove the placeholder element
        previewContainer.select(elementIdentifiers.ownerPage.orderId).remove();
    };

    const updateName = (
        previewContainer: D3Selection,
        calendar: CalendarState,
    ) => {
        previewContainer
            .select(elementIdentifiers.ownerPage.name)
            .select("tspan")
            .text(calendar.ownerPage.name);
    };

    const updateEmail = (
        previewContainer: D3Selection,
        calendar: CalendarState,
    ) => {
        previewContainer
            .select(elementIdentifiers.ownerPage.email)
            .select("tspan")
            .text(calendar.ownerPage.email);
    };

    const updatePhoneNumber = (
        previewContainer: D3Selection,
        calendar: CalendarState,
    ) => {
        previewContainer
            .select(elementIdentifiers.ownerPage.phone)
            .select("tspan")
            .text(calendar.ownerPage.phoneNumber);
    };

    const updateGreeting = (
        previewContainer: D3Selection,
        calendar: CalendarState,
    ) => {
        previewContainer
            .select(elementIdentifiers.ownerPage.greeting)
            .select("tspan")
            .text(calendar.ownerPage.greeting || "");
    };

    const updateImage = (
        previewContainer: D3Selection,
        ownerPageImageUrl: string | null,
    ) => {
        if (ownerPageImageUrl !== null && ownerPageImageUrl !== "") {
            const imageElement = previewContainer.select(
                elementIdentifiers.ownerPage.ownerImage,
            );
            imageElement.attr(
                "href",
                ownerPageImageUrl != null ? ownerPageImageUrl : "",
            );
        }
    };

    useEffect(() => {
        if (divRef) {
            drawOwnerPage(divRef);
        }
    }, [ownerPage, calendar.ownerPage, ownerPageImageUrl]);

    const svgContainerCn = () =>
        cx({
            svgContainer: true,
            "svg-container": true,
        });

    const calendarContainerCn = () =>
        cx({
            calendarContainer: true,
        });

    return (
        <div key={"owner-page"} className={calendarContainerCn()}>
            <div className={svgContainerCn()} ref={(ref) => setDivRef(ref)} />
        </div>
    );
};

const mapStateToProps = (state: ApplicationState) => ({
    calendar: getCalendar(state),
    spiralItem: getSpiralItem(state),
    ownerPage: getOwnerPage(state),
    ownerPageImageUrl: getOwnerPageImageUrl(state),
});

const mapDispatchToProps = {
    ...actionCreators,
    ...uiActionCreators,
};

export default connect(mapStateToProps, mapDispatchToProps)(OwnerPage);
